import { formatUrl, route } from './route';

const forObligation = (obligationId: number) => ({ id: String(obligationId) });

const forObligor = (obligationId: number, obligorId: number) => ({
    id: String(obligationId),
    obligorId: String(obligorId)
});

const forJournal = (obligationId: number, journalId: number) => ({
    id: String(obligationId),
    journalId: String(journalId)
});

const forPayment = (obligationId: number, paymentId: number) => ({
    id: String(obligationId),
    paymentId: String(paymentId)
});

const forActivity = (obligationId: number, activityId: number) => ({
    id: String(obligationId),
    activityid: String(activityId)
});

export const obligationRoutes = {
    detail: (obligationId: number) => route('/(app)/obligations/[id=integer]', forObligation(obligationId)),
    activities: (obligationId: number) =>
        route('/(app)/obligations/[id=integer]/activities', forObligation(obligationId)),
    auditHistory: (obligationId: number) =>
        route('/(app)/obligations/[id=integer]/audit-history', forObligation(obligationId)),
    communicationPreferences: (obligationId: number) =>
        route('/(app)/obligations/[id=integer]/communication', forObligation(obligationId)),
    creditReportHistory: (obligationId: number) =>
        route('/(app)/obligations/[id=integer]/credit-report-history', forObligation(obligationId)),
    customerPortalDetails: (obligationId: number) =>
        route('/(app)/obligations/[id=integer]/customerPortal', forObligation(obligationId)),
    data: (obligationId: number) => route('/(app)/obligations/[id=integer]/data', forObligation(obligationId)),
    details: (obligationId: number) => route('/(app)/obligations/[id=integer]/details', forObligation(obligationId)),
    related: (obligationId: number) => route('/(app)/obligations/[id=integer]/related', forObligation(obligationId)),
    workflowInstances: (obligationId: number) =>
        route('/(app)/obligations/[id=integer]/workflows', forObligation(obligationId)),
    adjustment: (obligationId: number, params?: URLSearchParams) =>
        formatUrl(route('/(app)/obligations/[id=integer]/adjustment', forObligation(obligationId)), params),
    ledger: (obligationId: number, ledger: string) =>
        route('/(app)/obligations/[id=integer]/ledgers/[ledger]', { ...forObligation(obligationId), ledger }),

    documents: {
        list: (obligationId: number) => route('/(app)/obligations/[id=integer]/documents', forObligation(obligationId)),
        statements: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/documents/statements', forObligation(obligationId))
    },

    contacts: {
        list: (obligationId: number, params?: URLSearchParams) =>
            formatUrl(route('/(app)/obligations/[id=integer]/contacts', forObligation(obligationId)), params),
        add: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/contacts/add', forObligation(obligationId)),
        forObligor: (obligationId: number, obligorId: number) =>
            formatUrl(
                route('/(app)/obligations/[id=integer]/contacts', forObligation(obligationId)),
                new URLSearchParams({ obligorId: String(obligorId) })
            ),
        details: (obligationId: number, obligorId: number, params?: URLSearchParams) =>
            formatUrl(
                route(
                    '/(app)/obligations/[id=integer]/contacts/[obligorId=integer]/details',
                    forObligor(obligationId, obligorId)
                ),
                params
            ),
        addresses: (obligationId: number, obligorId: number, params?: URLSearchParams) =>
            formatUrl(
                route(
                    '/(app)/obligations/[id=integer]/contacts/[obligorId=integer]/addresses',
                    forObligor(obligationId, obligorId)
                ),
                params
            ),
        emailAddresses: (obligationId: number, obligorId: number, params?: URLSearchParams) =>
            formatUrl(
                route(
                    '/(app)/obligations/[id=integer]/contacts/[obligorId=integer]/emailaddresses',
                    forObligor(obligationId, obligorId)
                ),
                params
            ),
        notes: (obligationId: number, obligorId: number, params?: URLSearchParams) =>
            formatUrl(
                route(
                    '/(app)/obligations/[id=integer]/contacts/[obligorId=integer]/notes',
                    forObligor(obligationId, obligorId)
                ),
                params
            ),
        phoneNumbers: (obligationId: number, obligorId: number, params?: URLSearchParams) =>
            formatUrl(
                route(
                    '/(app)/obligations/[id=integer]/contacts/[obligorId=integer]/phonenumbers',
                    forObligor(obligationId, obligorId)
                ),
                params
            )
    },

    journals: {
        create: (obligationId: number, ledgerCode?: string) => {
            const base = route('/(app)/obligations/[id=integer]/journals/new', forObligation(obligationId));
            return ledgerCode ? formatUrl(base, new URLSearchParams({ ledgerCode })) : base;
        },
        detail: (obligationId: number, journalId: number) =>
            route('/(app)/obligations/[id=integer]/journals/[journalId=integer]', forJournal(obligationId, journalId)),
        edit: (obligationId: number, journalId: number) =>
            route(
                '/(app)/obligations/[id=integer]/journals/[journalId=integer]/edit',
                forJournal(obligationId, journalId)
            )
    },

    payments: {
        list: (obligationId: number) => route('/(app)/obligations/[id=integer]/payments', forObligation(obligationId)),
        create: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/payments/payment', forObligation(obligationId)),
        other: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/payments/other', forObligation(obligationId)),
        refund: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/payments/refund', forObligation(obligationId)),
        methods: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/payments/methods', forObligation(obligationId)),
        depositInfo: (obligationId: number, paymentId: number) =>
            route(
                '/(app)/obligations/[id=integer]/payments/[paymentId=integer]/deposit-info',
                forPayment(obligationId, paymentId)
            ),
        reverse: (obligationId: number, paymentId: number) =>
            route(
                '/(app)/obligations/[id=integer]/payments/[paymentId=integer]/reverse',
                forPayment(obligationId, paymentId)
            ),

        autopays: {
            list: (obligationId: number) =>
                route('/(app)/obligations/[id=integer]/payments/autopays', forObligation(obligationId)),
            create: (obligationId: number) =>
                route('/(app)/obligations/[id=integer]/payments/autopays/new', forObligation(obligationId)),
            edit: (obligationId: number, autopayId: number) =>
                route('/(app)/obligations/[id=integer]/payments/autopays/[autopayId=integer]', {
                    ...forObligation(obligationId),
                    autopayId: String(autopayId)
                })
        }
    },

    transactions: {
        list: (
            obligationId: number,
            options: { activityIdToHighlight?: number | null; paymentIdToHighlight?: number | null } = {}
        ) => {
            const path = route('/(app)/obligations/[id=integer]/transactions', forObligation(obligationId));
            const params = new URLSearchParams();

            if (options.activityIdToHighlight) {
                params.set('locateActivity', options.activityIdToHighlight.toString());
            }

            if (options.paymentIdToHighlight) {
                params.set('locatePayment', options.paymentIdToHighlight.toString());
            }

            return params.size ? formatUrl(path, params) : path;
        },
        create: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/transactions/new', forObligation(obligationId)),
        print: (obligationId: number) =>
            route('/(app)/obligations/[id=integer]/transactions/print', forObligation(obligationId)),
        edit: (obligationId: number, activityId: number) =>
            route(
                '/(app)/obligations/[id=integer]/transactions/[activityid=integer]/edit',
                forActivity(obligationId, activityId)
            ),
        return: (obligationId: number, activityId: number) =>
            route(
                '/(app)/obligations/[id=integer]/transactions/[activityid=integer]/return',
                forActivity(obligationId, activityId)
            )
    }
};
